import { useEffect, useState } from 'react';
import { menuApi } from '../../lib/menuApi';
import MenuErrorBanner from './MenuErrorBanner';

/**
 * Pick a starter template (GET /api/menu/templates: Steakhouse, Family
 * Restaurant, Bar & Drinks, Café). Applying creates the template's categories
 * only (no items; idempotent), then calls onApplied(result).
 */
export default function TemplatePicker({ onApplied, onSkip }) {
    const [templates, setTemplates] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        menuApi.templates()
            .then((list) => { if (live) setTemplates(list); })
            .catch((err) => { if (live) { setError(err); setTemplates([]); } });
        return () => { live = false; };
    }, []);

    const apply = async (template) => {
        setBusy(template.id);
        setError(null);
        try {
            const result = await menuApi.applyTemplate(template.id);
            onApplied?.(result, template);
        } catch (err) {
            setError(err);
        } finally {
            setBusy('');
        }
    };

    return (
        <section aria-labelledby="template-picker-title" className="space-y-4">
            <div>
                <h3 id="template-picker-title" className="text-lg font-bold text-gray-900">Start from a template</h3>
                <p className="text-sm text-gray-600">
                    A template adds menu categories only. You add your own dishes and drinks next, or import them.
                </p>
            </div>
            <MenuErrorBanner error={error} />
            {templates === null && <p role="status" className="text-gray-500">Loading templates…</p>}
            <ul className="grid gap-3 sm:grid-cols-2">
                {(templates || []).map((t) => (
                    <li key={t.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-col gap-3">
                        <h4 className="font-bold text-gray-900">{t.name}</h4>
                        <ul className="flex flex-wrap gap-1.5" aria-label={`${t.name} categories`}>
                            {t.categories.map((c) => (
                                <li key={c.name} className={`${c.color} text-white text-xs font-semibold px-2 py-1 rounded-full`}>{c.name}</li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            onClick={() => apply(t)}
                            disabled={!!busy}
                            aria-label={`Use the ${t.name} template`}
                            className="mt-auto bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                        >
                            {busy === t.id ? 'Adding categories…' : 'Use this template'}
                        </button>
                    </li>
                ))}
            </ul>
            {onSkip && (
                <button type="button" onClick={onSkip} className="text-purple-700 font-semibold underline focus:outline-none focus:ring-2 focus:ring-purple-400 rounded">
                    Skip, I will build my menu myself
                </button>
            )}
        </section>
    );
}
